import React, { useEffect, useRef } from 'react';
import { ArrowRight, Target, Zap, BarChart3, Handshake, RefreshCcw } from 'lucide-react';

const IntroSection = () => {
  const revealRefs = useRef([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('opacity-100', 'translate-y-0');
            entry.target.classList.remove('opacity-0', 'translate-y-10');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );

    revealRefs.current.forEach((el) => {
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const addRef = (el) => {
    if (el && !revealRefs.current.includes(el)) {
      revealRefs.current.push(el);
    }
  };

  const introCards = [
    { icon: Target, title: 'Pensamos en tu objetivo', desc: 'Cada decisión que tomamos tiene un único norte: hacer crecer tu empresa' },
    { icon: Zap, title: 'Tecnología que genera dinero', desc: 'No implementamos herramientas. Implementamos sistemas que trabajan por ti 24/7' },
    { icon: BarChart3, title: 'Medimos todo, siempre', desc: 'Si no se puede medir, no se puede mejorar. Datos reales, decisiones inteligentes' },
    { icon: Handshake, title: 'Somos tu equipo interno externo', desc: 'Disponibilidad real, comunicación directa, propiedad total de resultados' },
    { icon: RefreshCcw, title: 'Crecemos cuando tú creces', desc: 'Nuestro éxito está directamente vinculado al tuyo. Sin excusas' }
  ];

  return (
    <section id="nosotros" className="section-padding bg-[#0A0A0A] relative">
      <div className="container-custom">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-20 items-center">
          {/* Left Side */}
          <div ref={addRef} className="transition-all duration-700 ease-out animate-fade-in-up">
            {/* Label */}
            <div className="flex items-center gap-3 mb-6">
              <div className="w-8 h-[1px] bg-[#D4AF37]"></div>
              <span className="font-outfit text-[0.72rem] uppercase tracking-[0.25em] text-[#D4AF37] font-medium">
                Por qué Evolvance es diferente
              </span>
            </div>

            <h2 className="heading-1 mb-7">No contratamos. Nos integramos.</h2>

            <p className="text-base leading-relaxed text-[#DBCE97] mb-6">
              Cuando confías en Evolvance, no estás subcontratando un proveedor. <strong className="text-white font-medium">Nos convertimos en parte de tu empresa</strong>: pensamos en tus objetivos, analizamos tus números y tomamos decisiones como si lleváramos el apellido de tu marca.
            </p>
            <p className="text-base leading-relaxed text-[#DBCE97] mb-8">
              Trabajamos <strong className="text-white font-medium">orientados a resultados medibles</strong>. Sin humo, sin promesas vacías. Si los KPIs no se cumplen, somos los primeros en decirlo — y los primeros en salir si no estamos aportando valor real.
            </p>

            {/* Promise */}
            <div className="bg-[#1A1A1A] border-l-[3px] border-[#D4AF37] p-5 pl-6">
              <strong className="block text-[#D4AF37] text-[0.8rem] tracking-[0.15em] uppercase mb-2">Nuestro compromiso contigo</strong>
              <p className="text-sm leading-relaxed text-white">
                Orientación total a resultados. Si no crecemos juntos, no tiene sentido continuar. Esta es la única forma en que sabemos trabajar.
              </p>
            </div>
          </div>

          {/* Right Side - Card Stack */}
          <div ref={addRef} className="transition-all duration-700 ease-out delay-200 animate-fade-in-up">
            <div className="flex flex-col gap-[3px]">
              {introCards.map((card, index) => {
                const IconComponent = card.icon;
                return (
                  <div
                    key={index}
                    className="bg-[#1A1A1A] p-6 flex items-center gap-4 transition-all duration-300 cursor-default border border-transparent hover:border-[#D4AF37]/30 hover:translate-x-1.5"
                  >
                    <div className="w-11 h-11 bg-[#D4AF37]/10 rounded flex items-center justify-center flex-shrink-0">
                      <IconComponent className="w-5 h-5 text-[#D4AF37]" />
                    </div>
                    <div>
                      <strong className="block text-sm text-white font-medium mb-1">{card.title}</strong>
                      <span className="text-[0.8rem] text-[#DBCE97]">{card.desc}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const Services = () => {
  const revealRefs = useRef([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry, i) => {
          if (entry.isIntersecting) {
            setTimeout(() => {
              entry.target.classList.add('opacity-100', 'translate-y-0');
              entry.target.classList.remove('opacity-0', 'translate-y-10');
            }, i * 80);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );

    revealRefs.current.forEach((el) => {
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const addRef = (el) => {
    if (el && !revealRefs.current.includes(el)) {
      revealRefs.current.push(el);
    }
  };

  const serviciosTop = [
    {
      num: '01',
      cat: 'Marketing',
      title: 'Tu marca se convierte en la referencia de tu sector',
      desc: 'Dejamos de ser uno más. Construimos una identidad que comunica autoridad, genera confianza y atrae clientes que ya quieren trabajar contigo antes de la primera llamada.',
      tags: [
        'Identidad de marca e imagen corporativa',
        'Gestión de redes sociales (todas las plataformas)',
        'Campañas de publicidad Meta · Google · LinkedIn',
        'Google Business · reputación local y reseñas',
        'Red de contactos LinkedIn B2B'
      ]
    },
    {
      num: '02',
      cat: 'Captación',
      title: 'Clientes cualificados llegando solos mientras duermes',
      desc: 'Sistemas de prospección automatizados que filtran, nutren y convierten leads sin que muevas un dedo. Tu pipeline lleno de oportunidades reales.',
      tags: [
        'Funnel de captación automatizado',
        'Email marketing y nurturing inteligente',
        'Chatbots y agentes IA de cualificación'
      ]
    },
    {
      num: '03',
      cat: 'Experiencia Inmersiva',
      title: 'El impacto que los competidores no se atreven a ofrecer',
      desc: 'Tours virtuales 360º, experiencias inmersivas y marketing visual de alto impacto que hacen que tu producto o espacio sea irresistible antes de que el cliente llegue.',
      tags: [
        'Tours virtuales y visitas 360º',
        'Marketing inmersivo e interactivo',
        'Presentaciones digitales de alto impacto'
      ]
    }
  ];

  const serviciosBottom = [
    {
      num: '04',
      cat: 'Software a Medida',
      title: 'Tu empresa funciona sola. Tú solo decides a dónde creces.',
      desc: 'Desarrollamos el software exacto que tu empresa necesita: sin excesos, sin limitaciones. CRMs que centralizan todo, webs que convierten y sistemas de gestión que eliminan el caos operativo para siempre.',
      tags: [
        'Desarrollo web y landing pages de alta conversión',
        'CRMs personalizados',
        'Sistemas de gestión a medida para tu sector'
      ]
    },
    {
      num: '05',
      cat: 'Automatización IA',
      title: 'Elimina el trabajo repetitivo. Multiplica lo que te genera dinero.',
      desc: 'Conectamos todas las partes de tu negocio con flujos inteligentes. Lo que antes requería horas de trabajo manual, ahora ocurre automáticamente. Escala sin contratar más personas.',
      tags: [
        'Asistentes de voz y chatbots con IA',
        'Integraciones con CRM',
        'Automatizaciones marketing',
        'Integración de herramientas y flujos 360º'
      ]
    }
  ];

  const ServiceCard = ({ service, large = false }) => (
    <div
      ref={addRef}
      className={`transition-all duration-700 ease-out bg-[#0A0A0A] ${large ? 'p-12 md:p-14' : 'p-10 md:p-12'} relative overflow-hidden cursor-default border border-transparent hover:border-[#D4AF37]/20 hover:translate-y-[-4px] group animate-fade-in-up`}
    >
      {/* Top gold line on hover */}
      <div className="absolute top-0 left-0 right-0 h-[2px] bg-[#D4AF37] transform scale-x-0 group-hover:scale-x-100 transition-transform duration-400"></div>

      {/* Number */}
      <div className="absolute top-5 right-6 font-outfit font-black text-[3.5rem] leading-none text-[#D4AF37]/[0.07] group-hover:text-[#D4AF37]/[0.14] transition-colors duration-400">
        {service.num}
      </div>

      {/* Category */}
      <div className="text-[0.68rem] uppercase tracking-[0.2em] text-[#D4AF37] mb-4 opacity-80">
        {service.cat}
      </div>

      {/* Title */}
      <h3 className="font-outfit font-extrabold text-xl leading-tight mb-4 text-white">
        {service.title}
      </h3>

      {/* Description */}
      <p className="text-sm leading-relaxed text-[#DBCE97] mb-6">
        {service.desc}
      </p>

      {/* Tags */}
      <div className="flex flex-col gap-[7px]">
        {service.tags.map((tag, i) => (
          <span key={i} className="text-[0.75rem] text-[#eee] flex items-center gap-2">
            <span className="text-[#D4AF37] text-[0.7rem]">—</span>
            {tag}
          </span>
        ))}
      </div>
    </div>
  );

  return (
    <>
      {/* INTRO SECTION */}
      <IntroSection />

      {/* SERVICIOS */}
      <section id="servicios" className="section-padding bg-[#1A1A1A] relative">
        <div className="container-custom">
          {/* Section Header */}
          <div ref={addRef} className="transition-all duration-700 ease-out text-center mb-16 md:mb-20 animate-fade-in-up">
            <div className="font-outfit text-[0.72rem] uppercase tracking-[0.25em] text-[#D4AF37] mb-4">
              Lo que hacemos por tu empresa
            </div>
            <h2 className="heading-1 mb-4">
              <span className="sr-only">Expertos en Desarrollo Web y Aplicaciones a Medida en Córdoba y Granada - </span>
              Cada servicio diseñado<br className="hidden md:block" /> para un resultado concreto
            </h2>
            <p className="text-base text-[#DBCE97] max-w-[560px] mx-auto leading-relaxed">
              No vendemos herramientas. Vendemos el impacto que esas herramientas generan en tu facturación, en tu tiempo y en tu competitividad.
            </p>
          </div>

          {/* Top Grid - 3 columns */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-[2px] mb-[2px]">
            {serviciosTop.map((service, index) => (
              <ServiceCard key={index} service={service} />
            ))}
          </div>

          {/* Bottom Grid - 2 columns */}
          <div className="grid md:grid-cols-2 gap-[2px]">
            {serviciosBottom.map((service, index) => (
              <ServiceCard key={index} service={service} large />
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default Services;
